import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import {
  getSiteURL,
  truncateMarketingRemarks,
  processMetaTitle,
  buildCanonicalUrl
} from '@lib/utils';
import {
  generateWebsiteSchema,
  generateRealEstateSchema,
  generateAgentSchema,
  generateAboutSchema,
  generateContactSchema,
  generateOfficeSchema,
  generateCommunityEventSchema,
  generateItemListSchema
} from '@lib/generate-schemas';
import { gtmVirtualPageView } from '@lib/gtm';

// ---------------------------------------------------------

import { component as Footer } from './footer';
import { component as Header } from './header';
import AlertBanner from '@components/alert-banner';

const DebugMediaQueries = dynamic(() => import('./debug-media-queries'), {
  loading: () => <></>
});

// ---------------------------------------------------------

import {
  theme_gradient,
  preview_alert,
  container_footer_at_bottom,
  main,
  preview_desktop_title,
  preview_mobile_title
} from './styles.module.scss';
import Button from '@components/button';

const themeOptions = {
  gradient: theme_gradient,
  none: null
};

const isBeta = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'staging';

// ---------------------------------------------------------

const Layout = ({
  canonicalUrl,
  children,
  meta,
  nofollow,
  theme = 'none',
  noCanonical = false,
  collection = 'Main',
  preview,
  dynamicOgImage,
  homePath,
  specializedPageSchema,
  cmsSchema,
  listingDetails,
  agentDetails,
  officeDetails,
  eventSchemaData,
  itemListData,
  is404Page,
  searchResultsSchema,
  breadcrumbSchema,
  communitySchema,
  faqSchema,
  serviceSchema,
  paginationLinks
}) => {
  // -------------------------------------------------------

  const classes = classNames(main, {
    [themeOptions[theme]]: themeOptions[theme]
  });

  // -------------------------------------------------------

  const router = useRouter();

  const excludedQueryParams = ['agentid', 'modal', 'sortby'];

  // Flex pages live under the internal /static/[slug] route and are exposed at
  // clean URLs via rewrites. Strip the /static prefix so the canonical (and
  // og:url) point at the public URL rather than the internal path.
  const publicPath = router.asPath.replace(/^\/static(?=\/|$)/, '');

  const generatedCanonicalUrl =
    publicPath === `/` || publicPath === ''
      ? getSiteURL()
      : buildCanonicalUrl(getSiteURL() + publicPath, excludedQueryParams);

  let imageURL =
    meta?.image?.url ||
    'https://images.ctfassets.net/wzzep3ntdgx4/5Eb7vSONDVgHo19WjNWS0h/21d57ff840dde6ace67a8d41dc468fdb/sibcyclinerealtors.png';

  const collectionDefaults = {
    Main: {
      title: 'Homes for Sale, Mortgage, Insurance & More | Sibcy Cline REALTORS®',
      description:
        '#1 Local Real Estate Search site in Cincinnati, Dayton, NKY, & SE Indiana. Find a real estate agent. Get pre-approved for a loan. Shop insurance rates.',
      siteName: 'Sibcy Cline REALTORS®',
      titleSuffix: 'Sibcy Cline REALTORS®'
    },
    HomeServices: {
      title: 'Home Services | Sibcy Cline',
      description:
        'Professional home services from Sibcy Cline. Find trusted contractors, inspectors, and home service professionals in Cincinnati, Dayton, and Northern Kentucky.',
      siteName: 'Sibcy Cline Home Services',
      titleSuffix: 'Sibcy Cline Home Services'
    },
    Mortgage: {
      title: 'Mortgage Services | Sibcy Cline',
      description:
        'Get pre-approved for a mortgage with Sibcy Cline. Competitive rates and expert guidance for home buyers in Cincinnati, Dayton, NKY, & SE Indiana.',
      siteName: 'Sibcy Cline Mortgage Services',
      titleSuffix: 'Sibcy Cline Mortgage Services'
    },
    Insurance: {
      title: 'Insurance Services | Sibcy Cline',
      description:
        'Compare insurance rates and get coverage with Sibcy Cline. Home, auto, and more in Cincinnati, Dayton, NKY, & SE Indiana.',
      siteName: 'Sibcy Cline Insurance',
      titleSuffix: 'Sibcy Cline Insurance'
    },
    Relocation: {
      title: 'Relocation Services | Sibcy Cline',
      description:
        'Relocating to Cincinnati, Dayton, or Northern Kentucky? Sibcy Cline relocation services make your move seamless.',
      siteName: 'Sibcy Cline Relocation Services',
      titleSuffix: 'Sibcy Cline Relocation Services'
    },
    Title: {
      title: 'Title Services | Sibcy Cline',
      description:
        'Professional title services from Sibcy Cline. Secure your real estate transaction in Cincinnati, Dayton, NKY, & SE Indiana.',
      siteName: 'Sibcy Cline Title Services',
      titleSuffix: 'Sibcy Cline Title Services'
    }
  };

  const currentDefaults = collectionDefaults[collection] || collectionDefaults.Main;

  const getMetaTitle = () => {
    if (collection !== 'Main') {
      return meta?.title || currentDefaults.title;
    }

    if (router.asPath.includes('/listing/')) {
      return processMetaTitle(meta?.title || currentDefaults.title);
    } else {
      return (
        (meta?.title && `${meta.title} | ${currentDefaults.titleSuffix}`) || currentDefaults.title
      );
    }
  };

  const nextMeta = {
    canonical: canonicalUrl || (!noCanonical && generatedCanonicalUrl) || null,
    description: meta?.description || currentDefaults.description,
    imageAlt: meta?.image?.alt || '',
    imageUrl: dynamicOgImage || imageURL,
    title: getMetaTitle(),
    url: canonicalUrl || getSiteURL() + publicPath
  };

  // Define the schema generators for each specialized page
  const schemaGenerators = {
    websiteSchema: generateWebsiteSchema, // /
    realEstateSchema: generateRealEstateSchema, // /listing/[id]
    agentSchema: generateAgentSchema, // /agent/[id]
    aboutSchema: generateAboutSchema, // /about
    contactSchema: generateContactSchema, // /contact
    officeSchema: generateOfficeSchema, // /office/[slug] and /offices
    communityEventSchema: generateCommunityEventSchema, // /community/[slug]
    itemListSchema: generateItemListSchema // community/development carousels
  };

  // Change from single schema to array
  let schemas = [];

  // Handle specialized page schema (primary schema)
  let specializedSchema = null;
  if (specializedPageSchema) {
    const generateSchema = schemaGenerators[specializedPageSchema];

    if (generateSchema) {
      let outputSchema;
      if (listingDetails) {
        outputSchema = generateSchema(listingDetails);
      } else if (agentDetails) {
        outputSchema = generateSchema(agentDetails);
      } else if (officeDetails) {
        outputSchema = generateSchema(officeDetails);
      } else if (eventSchemaData) {
        outputSchema = generateSchema(eventSchemaData.events);
      } else if (itemListData) {
        outputSchema = generateSchema(itemListData);
      } else {
        outputSchema = generateSchema(nextMeta);
      }

      if (outputSchema) {
        specializedSchema = outputSchema;
      }
    }
  }

  // Handle itemList schema (additional schema)
  if (
    itemListData &&
    itemListData.items?.length > 0 &&
    specializedPageSchema !== 'itemListSchema'
  ) {
    const itemListSchema = generateItemListSchema(itemListData);
    if (itemListSchema) {
      schemas.push(itemListSchema);
    }
  }

  // Combine specialized and CMS schemas, then push to schemas array
  if (cmsSchema && specializedSchema) {
    try {
      const cmsParsed = JSON.parse(cmsSchema);
      const specializedParsed = JSON.parse(specializedSchema);

      const cmsGraph = cmsParsed['@graph'] || [cmsParsed];
      const specializedGraph = specializedParsed['@graph'] || [specializedParsed];

      schemas.push(
        JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [...specializedGraph, ...cmsGraph]
        })
      );
    } catch (error) {
      console.error('[Schema Injection] Failed to combine schemas:', error.message);
      schemas.push(specializedSchema);
    }
  } else if (cmsSchema) {
    schemas.push(cmsSchema);
  } else if (specializedSchema) {
    schemas.push(specializedSchema);
  }

  if (searchResultsSchema) schemas.push(searchResultsSchema);
  if (breadcrumbSchema) schemas.push(breadcrumbSchema);
  if (communitySchema) schemas.push(communitySchema);
  if (faqSchema) schemas.push(faqSchema);
  if (serviceSchema) schemas.push(serviceSchema);

  const noIndexNoFollow = isBeta || nofollow;

  useEffect(() => {
    // Wait for router to be ready before tracking
    if (!router.isReady || is404Page) {
      return;
    }

    const mainDataLayer = {
      url: router.pathname
    };

    gtmVirtualPageView(mainDataLayer);
  }, [router.isReady, router.pathname]);

  // -------------------------------------------------------

  return (
    <>
      <NextSeo
        canonical={noCanonical ? null : nextMeta.canonical}
        nofollow={noIndexNoFollow}
        noindex={noIndexNoFollow}
        openGraph={{
          images: [
            {
              alt: nextMeta.imageAlt,
              height: 630,
              url: nextMeta.imageUrl,
              width: 1200
            }
          ],
          siteName: currentDefaults.siteName,
          type: 'website',
          url: nextMeta.url
        }}
        title={nextMeta.title}
        description={truncateMarketingRemarks(nextMeta.description)}
        twitter={{
          cardType: 'summary_large_image',
          site: '@sibcycline'
        }}
        additionalLinkTags={[
          {
            href: '/feeds/rss.xml',
            rel: 'alternate',
            title: 'SibcyCline.com Blog RSS Feed',
            type: 'application/rss+xml'
          },
          ...(paginationLinks?.prev ? [{ rel: 'prev', href: paginationLinks.prev }] : []),
          ...(paginationLinks?.next ? [{ rel: 'next', href: paginationLinks.next }] : [])
        ]}
      />

      <div className={container_footer_at_bottom}>
        <Header collection={collection} homePath={homePath} />
        <AlertBanner />
        <main className={classes}>{children}</main>
        <Footer collection={collection} homePath={homePath} />
      </div>

      {preview && (
        <div className={preview_alert}>
          <h3>
            <span className={preview_desktop_title}>Viewing Preview</span>
            <span className={preview_mobile_title}>Preview</span>
          </h3>
          <Button label="Exit preview" url="/api/exit-preview" />
        </div>
      )}

      <DebugMediaQueries />

      {schemas.length > 0 && (
        <Head>
          {schemas.map((schema, idx) => (
            <script
              key={`schema-${idx}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: schema }}
            />
          ))}
        </Head>
      )}
    </>
  );
};

Layout.propTypes = {
  canonicalUrl: PropTypes.string,

  children: PropTypes.node.isRequired,

  /**
   * Collection name of the core services, to indicate the header style
   */
  collection: PropTypes.oneOf([
    'Main',
    'Mortgage',
    'Insurance',
    'Relocation',
    'Title',
    'HomeServices'
  ]),

  meta: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    title: PropTypes.string
  }),

  /**
   * Specifies if we don't want an auto generated canonical url added
   */
  noCanonical: PropTypes.bool,

  /**
   * Specifies if the page should contain a nofollow meta tag.
   */
  nofollow: PropTypes.bool,

  /**
   * Specifies the listing details for the LDP real estate schema
   */
  listingDetails: PropTypes.object,

  /**
   * Specifies the agent details for agent profile schema
   */
  agentDetails: PropTypes.object,

  /**
   * Specifies the office details for the office schema
   */
  officeDetails: PropTypes.object,

  /**
   * Specifies whether the page is shown in preview mode
   */
  preview: PropTypes.bool,

  /**
   * Specifies the theme.
   */
  theme: PropTypes.oneOf(Object.keys(themeOptions)),

  /**
   * Specifies the OG image.
   */
  dynamicOgImage: PropTypes.string,
  homePath: PropTypes.string,
  /**
   * Specifies the function used to generate the schema for the page.
   */
  specializedPageSchema: PropTypes.oneOf([
    'websiteSchema',
    'realEstateSchema',
    'agentSchema',
    'aboutSchema',
    'contactSchema',
    'officeSchema',

    'communityEventSchema',
    'itemListSchema'
  ]),

  /**
   * Specifies the event schema data for community pages
   */
  eventSchemaData: PropTypes.shape({
    events: PropTypes.array
  }),

  /**
   * Specifies the data for the ItemList schema
   */
  itemListData: PropTypes.shape({
    name: PropTypes.string,
    items: PropTypes.array
  }),

  /**
   * Specifies the CMS-provided schema JSON-LD string.
   */
  cmsSchema: PropTypes.string,

  /**
   * Pre-generated ItemList schema string for search results pages.
   */
  searchResultsSchema: PropTypes.string,

  /**
   * Pre-generated BreadcrumbList schema string.
   */
  breadcrumbSchema: PropTypes.string,

  /**
   * Pre-generated community @graph schema string for community pages.
   */
  communitySchema: PropTypes.string,

  /**
   * Pre-generated FAQPage schema string.
   */
  faqSchema: PropTypes.string,

  /**
   * Pre-generated Service schema string for service/program pages.
   */
  serviceSchema: PropTypes.string,

  /**
   * Pagination link URLs for rel="prev" and rel="next".
   */
  paginationLinks: PropTypes.shape({
    prev: PropTypes.string,
    next: PropTypes.string
  }),

  is404Page: PropTypes.bool
};

export default Layout;
